import type { KryspaceRenderManifest } from '../../../types/kryspaceRenderManifest';
import { fixUtf8Mojibake } from '../fixUtf8Mojibake';
import type { ControlBridgeQueueItem, ControlBridgeViewModel } from '../deriveViewModel';
import { stripSessionTypeParenthetical } from '../exoPanel/exoPanelHelpers';
import { isP0, sortByPriority } from '../queueHelpers';
import { statusPillLabel } from './missionControlHelpers';

export interface McNextMoveCard {
  itemId?: string;
  title: string;
  statusPhrase?: string;
  priority?: string;
  subtitle?: string;
  assignedResourceRef?: string;
}

export interface McNextMoveModel {
  primarySessionRef?: string;
  primarySessionLabel?: string;
  nextWaypoint: McNextMoveCard | null;
  criticalAction: McNextMoveCard | null;
  criticalBlocker: McNextMoveCard | null;
}

function fieldString(
  manifest: KryspaceRenderManifest,
  sectionId: string,
  key: string
): string | undefined {
  const section = manifest.sections.find((s) => s.id === sectionId);
  const field = section?.fields?.find((f) => f.key === key);
  const value = field?.value;
  if (value == null || value === '') return undefined;
  return fixUtf8Mojibake(String(value));
}

function cardFromFields(
  manifest: KryspaceRenderManifest,
  prefix: 'next_waypoint' | 'critical_action' | 'critical_blocker'
): McNextMoveCard | null {
  const titleKey =
    prefix === 'next_waypoint'
      ? 'next_waypoint_title'
      : prefix === 'critical_action'
        ? 'critical_action_title'
        : 'critical_blocker_title';
  const title = fieldString(manifest, 'mc-next-move', titleKey);
  if (!title) return null;

  const idKey =
    prefix === 'next_waypoint'
      ? 'next_waypoint_ref'
      : prefix === 'critical_action'
        ? 'critical_action_item_id'
        : 'critical_blocker_item_id';
  const statusKey =
    prefix === 'next_waypoint'
      ? 'next_waypoint_status'
      : prefix === 'critical_action'
        ? 'critical_action_status'
        : 'critical_blocker_status';
  const priorityKey =
    prefix === 'critical_action' ? 'critical_action_priority' : 'critical_blocker_priority';

  const expectedDate =
    prefix === 'next_waypoint'
      ? fieldString(manifest, 'mc-next-move', 'next_waypoint_expected_date')
      : undefined;

  return {
    itemId: fieldString(manifest, 'mc-next-move', idKey),
    title,
    statusPhrase: fieldString(manifest, 'mc-next-move', statusKey),
    priority: fieldString(manifest, 'mc-next-move', priorityKey),
    subtitle: expectedDate ? `Expected ${expectedDate}` : undefined,
    assignedResourceRef:
      prefix === 'critical_action'
        ? fieldString(manifest, 'mc-next-move', 'critical_action_assigned_resource_ref')
        : undefined,
  };
}

const AGENT_ASSIGNEE_TOKENS = ['prai-', 'krybot', 'cursor', 'agent:'];

function isAgentAssignee(ref?: string): boolean {
  const blob = (ref || '').toLowerCase();
  return AGENT_ASSIGNEE_TOKENS.some((token) => blob.includes(token));
}

/** Critical action is the viewer's own row. Agent assignees never fill the card. */
export function criticalActionForViewer(
  card: McNextMoveCard | null,
  eligible: Array<{
    itemId: string;
    title: string;
    statusPhrase?: string;
    priority?: string;
    assignedResourceRef?: string;
    res?: string;
  }>,
  viewerResourceRef?: string
): McNextMoveCard | null {
  const viewer = viewerResourceRef?.trim();
  const matchesViewer = (ref?: string) => Boolean(viewer) && ref?.trim() === viewer;
  if (card && !isAgentAssignee(card.assignedResourceRef) && (!viewer || matchesViewer(card.assignedResourceRef))) {
    return card;
  }
  if (!viewer) return null;
  const mine = eligible.find(
    (item) =>
      !isAgentAssignee(item.assignedResourceRef) &&
      (matchesViewer(item.assignedResourceRef) || matchesViewer(item.res))
  );
  if (!mine?.title) return null;
  return {
    itemId: mine.itemId,
    title: mine.title,
    statusPhrase: mine.statusPhrase,
    priority: mine.priority,
    assignedResourceRef: mine.assignedResourceRef,
  };
}

export function parseMcNextMoveFromManifest(manifest: KryspaceRenderManifest): McNextMoveModel | null {
  const section = manifest.sections.find((s) => s.id === 'mc-next-move');
  if (!section?.fields?.length) return null;

  const nextWaypoint = cardFromFields(manifest, 'next_waypoint');
  const criticalAction = cardFromFields(manifest, 'critical_action');
  const criticalBlocker = cardFromFields(manifest, 'critical_blocker');

  if (!nextWaypoint && !criticalAction && !criticalBlocker) return null;

  return {
    primarySessionRef: fieldString(manifest, 'mc-next-move', 'primary_bridge_session_ref'),
    primarySessionLabel: stripSessionTypeParenthetical(
      fieldString(manifest, 'mc-next-move', 'primary_bridge_session_label')
    ),
    nextWaypoint,
    criticalAction,
    criticalBlocker,
  };
}

function pickNextWaypoint(vm: ControlBridgeViewModel): McNextMoveCard | null {
  const active = vm.waypoints.find((wp) => /active|in.?progress|open|drafted|initialized/i.test(wp.statusPhrase));
  const candidate = active ?? vm.waypoints.find((wp) => !/closed|complete|done/i.test(wp.statusPhrase));
  const wp = candidate ?? vm.waypoints[0];
  if (!wp?.title) return null;
  return {
    itemId: wp.itemId,
    title: wp.title,
    statusPhrase: wp.statusPhrase,
    subtitle: wp.sequenceOrder != null ? `Waypoint ${wp.sequenceOrder}` : undefined,
  };
}

function pickCriticalActionItem(focus: ControlBridgeQueueItem[]): ControlBridgeQueueItem | null {
  const sorted = sortByPriority(focus);
  return sorted.find((item) => isP0(item.priority)) ?? sorted[0] ?? null;
}

function pickCriticalBlockerItem(
  issues: ControlBridgeQueueItem[],
  action: ControlBridgeQueueItem | null
): ControlBridgeQueueItem | null {
  let candidates = sortByPriority([...issues]);
  if (action) {
    const related = candidates.filter(
      (issue) =>
        (action.improvementRef && issue.improvementRef === action.improvementRef) ||
        (action.waypointRef && issue.waypointRef === action.waypointRef)
    );
    if (related.length) candidates = related;
  }
  return candidates[0] ?? null;
}

function queueItemToCard(item: ControlBridgeQueueItem, subtitle?: string): McNextMoveCard {
  return {
    itemId: item.itemId,
    title: item.title,
    statusPhrase: item.statusPhrase,
    priority: item.priority,
    subtitle: subtitle ?? item.improvementTitle ?? item.issueSummary ?? item.issueType,
  };
}

/** Proxy fallback when mc-next-move manifest section absent. */
export function deriveMcNextMoveFallback(
  vm: ControlBridgeViewModel,
  focus: ControlBridgeQueueItem[]
): McNextMoveModel | null {
  const nextWaypoint = pickNextWaypoint(vm);
  const actionItem = pickCriticalActionItem(focus);
  const criticalAction = actionItem ? queueItemToCard(actionItem) : null;
  const blockerItem = pickCriticalBlockerItem(vm.openIssues, actionItem);
  const criticalBlocker = blockerItem ? queueItemToCard(blockerItem) : null;

  if (!nextWaypoint && !criticalAction && !criticalBlocker) return null;

  const sessionRaw =
    typeof vm.rollup.bridgeSession === 'string' ? fixUtf8Mojibake(vm.rollup.bridgeSession.trim()) : undefined;
  const sessionLabel = stripSessionTypeParenthetical(sessionRaw);

  return {
    primarySessionLabel: sessionLabel,
    nextWaypoint,
    criticalAction,
    criticalBlocker,
  };
}

export function nextMovePriorityLabel(priority?: string): string | null {
  if (!priority?.trim()) return null;
  const normalized = priority.trim().toUpperCase();
  return normalized.startsWith('P') ? normalized : `P${normalized}`;
}

export function nextMoveStatusLabel(statusPhrase?: string): string {
  return statusPillLabel(statusPhrase);
}
